import {memo, useMemo} from "react";
import {Box, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import CakeOutlined from "@mui/icons-material/CakeOutlined";
import CachedIcon from "@mui/icons-material/Cached";
import LoginOutlined from "@mui/icons-material/LoginOutlined";
import LogoutOutlined from "@mui/icons-material/LogoutOutlined";
import HotelIcon from "@mui/icons-material/Hotel";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import {
    FloorplanReservationItemDTO
} from "crqlar-react-common/dist/models/floorplan/floorplanReservationItem.dto";
import {
    Reservation
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {
    getIconForLanguage,
    getNameForLanguage
} from "crqlar-react-common/dist/utils/translations.util";
import {
    ALLERGY_ICON_MAP,
    getAccommodationStayIndicators,
    getFloorplanGuestCounts,
    getReservationDisplay,
    getReservationRateLabel,
} from "../utils/reservation-utils";
import {useTranslation} from "react-i18next";
import {AgeCategoriesElement} from "../../../../common/uiElements/AgeCategoriesElement";
import {formatDateHM} from "crqlar-react-common/dist/utils/date.util";
import {useTimeframe} from "../hooks/useTimeframe";

interface Props {
    reservation: FloorplanReservationItemDTO;
    onClickEdit?: (reservation: Reservation) => void;
    isDragOverlay?: boolean;
    draggable?: boolean;
}

const ReadSidebarUnassignedItem: React.FC<Props> = ({
                                                        reservation,
                                                        onClickEdit,
                                                        isDragOverlay,
                                                        draggable = true,
                                                    }) => {
    const {t} = useTranslation();
    const {date: selectedDate} = useTimeframe();
    const displayGuestCounts = getFloorplanGuestCounts(reservation);
    const {
        guestDisplayName,
        primaryLanguage,
        end,
        timeRange,
        roomNames,
        roomNamesInline,
        fromBookingEngine,
        birthdayNames,
        isBirthday,
        preferences,
    } = getReservationDisplay({
        reservation: reservation.restaurantReservation,
        profiles: reservation.profiles,
        ageCategories: reservation.ageCategories,
        accommodationResources: reservation.accommodationResources,
        guestCounts: displayGuestCounts,
    });
    const now = new Date();
    const isPastItem = !isDragOverlay && !!end && end < now;
    const displayName = guestDisplayName || "—";
    const isSerial = Boolean(reservation.restaurantReservation?.reservationGroupId);
    const rateLabel = getReservationRateLabel(reservation);
    const {
        stayStart,
        stayEnd,
        checksInOnDate,
        checksOutOnDate,
    } = useMemo(
        () => getAccommodationStayIndicators(
            reservation.accommodationReservations,
            selectedDate,
        ),
        [reservation.accommodationReservations, selectedDate],
    );

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "stretch",
                opacity: isPastItem ? 0.6 : 1,
                ...(isDragOverlay
                    ? {
                        bgcolor: "#fff",
                        borderRadius: 2,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    }
                    : {
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        "&:hover": {bgcolor: "action.hover"},
                        "&:hover .edit-reveal": {
                            maxWidth: 32,
                            opacity: 1,
                        },
                        "&:focus-within .edit-reveal": {
                            maxWidth: 32,
                            opacity: 1,
                        },
                        "@media (hover: none)": {
                            "& .edit-reveal": {
                                maxWidth: 32,
                                opacity: 1,
                            },
                        },
                    }),
            }}
        >
            <Box sx={{pl: "16px", display: "flex", alignItems: "center"}}>
                <Box
                    sx={{
                        width: 10,
                        flex: "0 0 10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "text.secondary",
                    }}
                >
                    {draggable && <DragIndicatorIcon fontSize="small"/>}
                </Box>
            </Box>

            <Box sx={{flex: 1, minWidth: 0, pl: 2, pr: 1.5, py: 1.5}}>
                {/* Row 1: Flag + [Guest name + Icons] + Edit */}
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    minWidth: 0
                }}>
                    {primaryLanguage && (
                        <Box component="img"
                             alt={getNameForLanguage(primaryLanguage)}
                             src={getIconForLanguage(primaryLanguage)}
                             sx={{
                                 width: 14,
                                 height: 14,
                                 borderRadius: '50%',
                                 objectFit: 'cover',
                                 border: '0.5px solid rgba(0,0,0,0.15)',
                                 flex: "0 0 auto",
                                 marginTop: "-4px"
                             }}
                        />
                    )}
                    {/* Shrinkable group — clips as a unit so edit button always has room */}
                    <Box sx={{flex: "1 1 0", minWidth: 0, display: "flex", alignItems: "center", gap: 0.75, overflow: "hidden"}}>
                        <Typography noWrap title={displayName}
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: "0.9375rem",
                                        minWidth: 0,
                                        flex: "1 1 auto"
                                    }}>
                            {displayName}
                        </Typography>
                        {checksInOnDate && (
                            <Tooltip
                                title={t('{{name}} checks in at {{time}}', {
                                    name: displayName,
                                    time: stayStart ? formatDateHM(stayStart) : '—',
                                })}
                                arrow
                            >
                                <LoginOutlined sx={{
                                    fontSize: 14,
                                    color: "#0288d1",
                                    cursor: "default",
                                    flex: "0 0 auto",
                                }}/>
                            </Tooltip>
                        )}
                        {isBirthday && (
                            <Tooltip title={birthdayNames.map(name => t("It's {{name}}'s birthday", {name})).join(", ")} arrow>
                                <CakeOutlined sx={{fontSize: 14, color: "#9c27b0", cursor: "default", flex: "0 0 auto"}}/>
                            </Tooltip>
                        )}
                        {preferences.length > 0 && (
                            <Tooltip arrow title={
                                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                    {preferences.map((pref, i) => {
                                        const mapping = ALLERGY_ICON_MAP[pref.preferenceType!];
                                        if (!mapping) return null;
                                        const Icon = mapping.icon;
                                        return (
                                            <Stack key={`${pref.preferenceValue}-${i}`} direction="row" alignItems="center" gap={0.25}>
                                                <Icon sx={{fontSize: 12, color: mapping.color}}/>
                                                <Typography fontSize="0.7rem">{pref.preferenceValue}</Typography>
                                            </Stack>
                                        );
                                    })}
                                </Stack>
                            }>
                                <Stack direction="row" gap={0.25} sx={{cursor: "default", flex: "0 0 auto"}}>
                                    {Array.from(new Set(preferences.map(p => p.preferenceType))).map(type => {
                                        const mapping = ALLERGY_ICON_MAP[type!];
                                        if (!mapping) return null;
                                        const Icon = mapping.icon;
                                        return <Icon key={type} sx={{fontSize: 14, color: mapping.color}}/>;
                                    })}
                                </Stack>
                            </Tooltip>
                        )}
                        {checksOutOnDate && (
                            <Tooltip
                                title={t('{{name}} checks out at {{time}}', {
                                    name: displayName,
                                    time: stayEnd ? formatDateHM(stayEnd) : '—',
                                })}
                                arrow
                            >
                                <LogoutOutlined sx={{
                                    fontSize: 14,
                                    color: "#ef6c00",
                                    cursor: "default",
                                    flex: "0 0 auto",
                                }}/>
                            </Tooltip>
                        )}
                        {isSerial && (
                            <Tooltip title={t("Slot type SERIAL")} arrow>
                                <CachedIcon sx={{
                                    fontSize: 14,
                                    color: "text.primary",
                                    cursor: "default",
                                    flex: "0 0 auto",
                                }}/>
                            </Tooltip>
                        )}
                    </Box>
                    <Box
                        className="edit-reveal"
                        sx={{
                            maxWidth: 0,
                            opacity: 0,
                            overflow: "hidden",
                            display: "flex",
                            transition: "max-width 0.2s cubic-bezier(.4,0,.2,1), opacity 0.15s ease",
                        }}
                    >
                        <Tooltip title={t("Edit reservation")} arrow>
                            <IconButton size="small" sx={{p: 0.5}}
                                        onPointerDown={(e) => e.stopPropagation()}
                                        onClick={() => onClickEdit?.(reservation.restaurantReservation)}>
                                <EditIcon sx={{fontSize: 16}}/>
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Row 2: Room · Age categories · Time */}
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mt: 0.5,
                    minWidth: 0
                }}>
                    {fromBookingEngine && (<>
                        <Tooltip title={t("Online Reservation")} arrow>
                            <BookOnlineIcon sx={{
                                fontSize: 14,
                                color: "text.secondary",
                                flex: "0 0 auto",
                                cursor: "default"
                            }}/>
                        </Tooltip>
                        <Typography color="text.disabled" fontSize="0.75rem"
                                    sx={{flex: "0 0 auto"}}>·</Typography>
                    </>)}

                    {roomNames && (<>
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            minWidth: 0,
                            flex: "0 0 auto"
                        }}>
                            <HotelIcon sx={{
                                fontSize: 14,
                                color: "text.secondary",
                                flex: "0 0 auto"
                            }}/>
                            <Typography noWrap color="text.secondary"
                                        fontSize="0.75rem" title={roomNames}>
                                {roomNamesInline}
                            </Typography>
                        </Box>
                        <Typography color="text.disabled" fontSize="0.75rem"
                                    sx={{flex: "0 0 auto"}}>·</Typography>
                    </>)}

                    <AgeCategoriesElement
                        ageCategories={reservation.ageCategories}
                        ageCategoryCounts={displayGuestCounts}
                        aliasAgeCategories={reservation.aliasAgeCategories}
                        variant="inline"
                    />

                    <Typography color="text.disabled" fontSize="0.75rem"
                                sx={{flex: "0 0 auto"}}>·</Typography>

                    <Typography color="text.secondary" fontSize="0.75rem"
                                noWrap title={timeRange}
                                sx={{flex: rateLabel ? "0 1 auto" : "1 1 auto", minWidth: 0}}>
                        {timeRange}
                    </Typography>

                    {rateLabel && (<>
                        <Typography color="text.disabled" fontSize="0.75rem"
                                    sx={{flex: "0 0 auto"}}>·</Typography>
                        <Typography noWrap color="text.secondary" fontSize="0.75rem"
                                    sx={{flex: "1 1 auto", minWidth: 0}}
                                    title={rateLabel}>
                            {rateLabel}
                        </Typography>
                    </>)}
                </Box>
            </Box>
        </Box>
    );
};

export default memo(ReadSidebarUnassignedItem);
